import { AxiosInstance } from 'axios'
import { Image } from '../types/image'
import { PaginatedResponse } from '../types/api'

export type ImageRefreshStatus = 'pending' | 'running' | 'done' | 'failed' | 'skipped'

export type BulkRefreshRunStatus =
  | 'collecting'
  | 'running'
  | 'completed'
  | 'stopped'
  | 'failed'

export interface BulkRefreshProgress {
  status: BulkRefreshRunStatus
  /** Total images targeted by this run (or retry run). */
  total: number
  done: number
  failed: number
  /** Images skipped because they no longer exist (or have no original to refresh). */
  skipped: number
  runningIds: string[]
  imageStatuses: Record<string, ImageRefreshStatus>
  /** Set when the run itself failed (e.g. could not collect the image list). */
  error?: string
}

export interface BulkRefreshOptions {
  /** Concurrent requests per batch (5-10 recommended). */
  batchSize?: number
  /** Retry attempts per image after the initial call fails. */
  maxRetries?: number
  /** Base delay for exponential backoff between image retries (ms). */
  retryBaseDelayMs?: number
  onProgress?: (progress: BulkRefreshProgress) => void
  shouldStop?: () => boolean
}

export const DEFAULT_BATCH_SIZE = 5
export const DEFAULT_MAX_RETRIES = 2
export const DEFAULT_RETRY_BASE_DELAY_MS = 500

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function isNotFound(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { status?: number } }).response?.status === 'number' &&
    (error as { response: { status: number } }).response.status === 404
  )
}

/**
 * Fetches every page of the album's images so the refresh run knows the
 * full set up front (the UI only loads pages on scroll).
 */
export async function fetchAllAlbumImages(
  client: AxiosInstance,
  albumId: string
): Promise<Image[]> {
  const images: Image[] = []
  let cursor: string | undefined

  do {
    const { data } = await client.get('/images', {
      params: { albumId, limit: 100, cursor },
    })
    const page = data.data as PaginatedResponse<Image>
    images.push(...page.items)
    cursor = page.nextCursor
  } while (cursor)

  return images
}

/**
 * Refreshes one image, retrying transient failures with exponential backoff.
 * A 404 means the image no longer exists (or has no original file), so we
 * skip it rather than retry.
 */
export async function refreshImageWithRetry(
  client: AxiosInstance,
  imageId: string,
  options: Pick<BulkRefreshOptions, 'maxRetries' | 'retryBaseDelayMs'> = {}
): Promise<'done' | 'skipped'> {
  const maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES
  const baseDelay = options.retryBaseDelayMs ?? DEFAULT_RETRY_BASE_DELAY_MS

  for (let attempt = 0; ; attempt++) {
    try {
      await client.post(`/images/${imageId}/refresh-exif`)
      return 'done'
    } catch (error) {
      if (isNotFound(error)) {
        return 'skipped'
      }
      if (attempt >= maxRetries) {
        throw error
      }
      await sleep(baseDelay * 2 ** attempt)
    }
  }
}

type ProgressUpdate = Partial<Omit<BulkRefreshProgress, 'imageStatuses'>> & {
  imageStatuses?: Record<string, ImageRefreshStatus>
}

function makeEmitter(
  progress: BulkRefreshProgress,
  options: BulkRefreshOptions
): (update?: ProgressUpdate) => void {
  return (update) => {
    if (update) {
      Object.assign(progress, update)
    }
    options.onProgress?.({ ...progress, imageStatuses: { ...progress.imageStatuses } })
  }
}

/**
 * Processes image ids in bounded-concurrency batches, updating the progress
 * object in place. Sets `status: 'stopped'` if shouldStop() fires between
 * batches. Returns the list of ids that ended up failed, and whether the
 * run was stopped early.
 */
async function processBatches(
  client: AxiosInstance,
  imageIds: string[],
  progress: BulkRefreshProgress,
  emit: (update?: ProgressUpdate) => void,
  options: BulkRefreshOptions
): Promise<{ failedIds: string[]; stopped: boolean }> {
  const batchSize = options.batchSize ?? DEFAULT_BATCH_SIZE
  const failedIds: string[] = []

  for (let i = 0; i < imageIds.length; i += batchSize) {
    if (options.shouldStop?.()) {
      // Anything not yet processed goes back to pending so a retry/resume
      // can pick it up.
      for (const id of imageIds.slice(i)) {
        const status = progress.imageStatuses[id]
        if (status !== 'done' && status !== 'failed' && status !== 'skipped') {
          progress.imageStatuses[id] = 'pending'
        }
      }
      progress.runningIds = []
      progress.status = 'stopped'
      emit()
      return { failedIds, stopped: true }
    }

    const batch = imageIds.slice(i, i + batchSize)
    for (const id of batch) {
      progress.imageStatuses[id] = 'running'
    }
    progress.runningIds = [...batch]
    emit()

    const outcomes = await Promise.allSettled(
      batch.map(async (id) => refreshImageWithRetry(client, id, options))
    )

    progress.runningIds = []
    outcomes.forEach((outcome, index) => {
      const id = batch[index]
      if (outcome.status === 'fulfilled') {
        if (outcome.value === 'done') {
          progress.done++
          progress.imageStatuses[id] = 'done'
        } else {
          progress.skipped++
          progress.imageStatuses[id] = 'skipped'
        }
      } else {
        progress.failed++
        progress.imageStatuses[id] = 'failed'
        failedIds.push(id)
      }
    })
    emit()
  }

  return { failedIds, stopped: false }
}

/**
 * Runs a full bulk refresh for an album: collects all images across every
 * page, then refreshes them in batches. Never throws — all failures are
 * reflected in the returned progress.
 */
export async function runBulkRefresh(
  client: AxiosInstance,
  albumId: string,
  options: BulkRefreshOptions = {}
): Promise<BulkRefreshProgress> {
  const progress: BulkRefreshProgress = {
    status: 'collecting',
    total: 0,
    done: 0,
    failed: 0,
    skipped: 0,
    runningIds: [],
    imageStatuses: {},
  }
  const emit = makeEmitter(progress, options)
  emit()

  try {
    const images = (await fetchAllAlbumImages(client, albumId)).filter(
      (image) => image.originalKey
    )

    progress.total = images.length
    progress.status = 'running'
    emit()

    const result = await processBatches(
      client,
      images.map((image) => image.imageId),
      progress,
      emit,
      options
    )

    if (result.stopped) {
      // Stopped by the caller between batches.
      emit()
      return progress
    }
    progress.status = 'completed'
    emit()
    return progress
  } catch (error) {
    progress.status = 'failed'
    progress.error = error instanceof Error ? error.message : String(error)
    emit()
    return progress
  }
}

/**
 * Re-runs only the images that failed in a previous run. Keeps the overall
 * counters consistent: previously-failed images are given back to the pool,
 * then reprocessed like a fresh batch.
 */
export async function retryFailedBulkRefresh(
  client: AxiosInstance,
  previous: BulkRefreshProgress,
  options: BulkRefreshOptions = {}
): Promise<BulkRefreshProgress> {
  const failedIds = Object.entries(previous.imageStatuses)
    .filter(([, status]) => status === 'failed')
    .map(([id]) => id)

  const progress: BulkRefreshProgress = {
    status: 'running',
    total: previous.total,
    done: previous.done,
    // Handing the failures back for another attempt.
    failed: Math.max(0, previous.failed - failedIds.length),
    skipped: previous.skipped,
    runningIds: [],
    imageStatuses: {
      ...previous.imageStatuses,
      ...Object.fromEntries(failedIds.map((id) => [id, 'pending' as ImageRefreshStatus])),
    },
  }
  const emit = makeEmitter(progress, options)
  emit()

  try {
    const result = await processBatches(client, failedIds, progress, emit, options)
    if (result.stopped) {
      // Stopped by the caller between batches.
      emit()
      return progress
    }
    progress.status = 'completed'
    emit()
    return progress
  } catch (error) {
    progress.status = 'failed'
    progress.error = error instanceof Error ? error.message : String(error)
    emit()
    return progress
  }
}
